import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { T, useT } from '@transifex/react';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  applyAuthToken,
  redirectToPortalHome,
  requestOtp,
  verifyOtp,
} from 'src/api/auth';

import { emailLoginSchema, EmailLoginValues } from './schemas';
import { OtpStep } from './OtpStep';

type Props = { onRegister?: () => void };

// Passwordless citizen login: enter an email, receive a one-time code, exchange
// the code for a session token.
export const EmailCodeLogin: React.FC<Props> = ({ onRegister }) => {
  const tx = useT();
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<EmailLoginValues>({
    resolver: zodResolver(emailLoginSchema),
    defaultValues: { email: '' },
  });

  const onRequest = async (values: EmailLoginValues) => {
    setSubmitError(null);
    try {
      await requestOtp(values);
      setEmail(values.email);
      setStep('otp');
    } catch {
      setSubmitError(tx('Something went wrong'));
    }
  };

  if (step === 'otp') {
    return (
      <OtpStep
        email={email}
        onBack={() => {
          setStep('email');
          setSubmitError(null);
        }}
        onVerify={async (code) => {
          const { token } = await verifyOtp({ email, code });
          applyAuthToken(token);
          redirectToPortalHome();
        }}
      />
    );
  }

  return (
    <div className="wh-ui w-full">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onRequest)}
          className="flex w-full flex-col gap-4"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <T _str="Email" />
                </FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder={tx('you@example.com')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {submitError && (
            <p className="text-sm text-destructive">{submitError}</p>
          )}
          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="h-12 w-full"
          >
            {form.formState.isSubmitting ? (
              <T _str="Sending code…" />
            ) : (
              <T _str="Send code" />
            )}
          </Button>
          {onRegister && (
            <Button type="button" variant="link" onClick={onRegister}>
              <T _str="Create an account" />
            </Button>
          )}
        </form>
      </Form>
    </div>
  );
};
